import { set } from '@ember/object';
import { escapeHtml } from './strings.js';

/**
 * @module beebee-base/lib/utility/misc
 * @description Error handling, visibility/booking and fiscal-printer utilities.
 */


/** Mappatura status HTTP a chiavi i18n */
const HTTP_STATUS_MESSAGES = Object.freeze({
  0: 'beebeeboard error',
  500: 'beebeeboard error',
  401: 'operation not permitted',
  403: 'permission denied',
  404: 'item not found',
  422: 'invalid data'
});

/** Mappatura status HTTP a tipo notifica */
const HTTP_STATUS_NOTIFY_TYPE = Object.freeze({
  401: 'warning'
});

/**
 * Mostra una notifica di errore all'utente basata sulla risposta API
 * @param {Object} notify - Servizio notifiche
 * @param {Object} reason - Oggetto errore
 * @param {Object} intl - Servizio internazionalizzazione
 * @returns {boolean} True se la notifica è stata mostrata
 */
function notifyUserError(notify, reason, intl) {
  if (reason === undefined || !notify) {
    return false;
  }

  // Normalizza la risposta errore
  let response = reason;
  if (reason.errors?.[0]) {
    response = {
      status: reason.errors[0].status,
      title: reason.errors[0].title
    };
  } else if (reason.responseJSON?.errors?.[0]) {
    response = {
      status: reason.responseJSON.errors[0].status,
      title: reason.responseJSON.errors[0].title
    };
  }
  const status = parseInt(response.status);
  const messageKey = HTTP_STATUS_MESSAGES[status] ?? 'comunication error';
  const notifyType = HTTP_STATUS_NOTIFY_TYPE[status] ?? 'error';
  // Escape server-provided title: error responses can echo user input (XSS)
  const titleText = escapeHtml(response.title || '');
  notify[notifyType]({
    html: `${intl.t(messageKey)}<br /><small>${titleText}</small>`
  });
  return true;
}

/**
 * Applica gli errori di validazione JSON:API (HTTP 422) ai campi del model.
 *
 * Le API `*_all` rispondono con:
 *   { "errors": [{ "status": "422",
 *                  "source": { "pointer": "/data/attributes/<field>" },
 *                  "title": "...", "detail": "..." }] }
 *
 * Per ogni errore con un pointer di attributo valido aggiunge il messaggio a
 * `model.errors.<field>` (DS.Errors), cosi' i template che fanno il binding su
 * `item.errors.<field>` colorano di rosso il campo e mostrano il messaggio.
 * Inoltre notifica all'utente con `notify.warning`.
 *
 * @param {Object} model - Ember Data model in errore
 * @param {Object} reason - jqXHR o oggetto errore (cerca responseJSON.errors / errors)
 * @param {Object} notify - Servizio notifiche
 * @returns {boolean} True se almeno un errore di validazione e' stato applicato al model
 */
function applyValidationErrors(model, reason, notify) {
  const errors = reason?.responseJSON?.errors || reason?.errors;
  if (!model || !Array.isArray(errors) || typeof model.errors?.add !== 'function') {
    return false;
  }
  let applied = false;
  const messages = [];
  errors.forEach(err => {
    const match = /\/data\/attributes\/(.+)$/.exec(err?.source?.pointer || '');
    const message = err?.detail || err?.title || '';
    if (match) {
      const field = match[1];
      model.errors.remove(field);
      model.errors.add(field, message);
      applied = true;
    }
    if (message) {
      messages.push(message);
    }
  });
  if (applied && notify) {
    // Escape server-provided detail strings before injecting into notify HTML
    notify.warning({
      html: messages.map(escapeHtml).join('<br />')
    });
  }
  return applied;
}

/**
 * Verifica se mostrare il booking online
 * @param {Object} applicationController - Controller applicazione
 * @param {string|number} status - Status da verificare
 * @returns {boolean}
 */
function checkShowOnlineBooking(applicationController, status) {
  if (!applicationController) {
    return false;
  }
  if (applicationController.isSkiSchool && !applicationController.isGuideMonteRosa) {
    return false;
  }
  if (applicationController.isClinic && status !== 1 && status !== '1') {
    return false;
  }
  return true;
}

/**
 * Inizializza e verifica lo stato dei registratori di cassa
 * @param {Array} registratoriRT - Array di registratori
 * @returns {Promise<Array>} Registratori aggiornati con stato
 */
async function initializeAndCheckRegistratori(registratoriRT) {
  // FiscalPrinter è un global iniettato dall'app desktop: senza guard l'accesso
  // lancia ReferenceError (vedi pattern in emetti-scontrino.js / get-scontrino.js).
  if (typeof FiscalPrinter === 'undefined') {
    return [];
  }
  if (!registratoriRT?.length) {
    return [];
  }
  const updatedRegistratoriRT = [];
  for (const registratoreRT of registratoriRT) {
    try {
      const printer = await FiscalPrinter.initializePrinter(registratoreRT.marca, {});
      set(registratoreRT, 'printer', printer);
      const enable = await printer.checkStatus({
        registratore: registratoreRT
      });
      set(registratoreRT, 'enable', enable.esito);

      // Auto-select if single enabled printer
      if (registratoriRT.length === 1 && enable.esito) {
        set(registratoreRT, 'selected', true);
      }
      updatedRegistratoriRT.push(registratoreRT);
    } catch (error) {
      console.error(`Errore inizializzazione registratore ${registratoreRT.name}:`, error);
    }
  }
  return updatedRegistratoriRT;
}

export { applyValidationErrors, checkShowOnlineBooking, initializeAndCheckRegistratori, notifyUserError };
//# sourceMappingURL=misc.js.map
